import React, { useEffect } from 'react';
import { useError } from '../../../error-provider/ErrorProvider';
import { Box, Flex, Stack, Switch, Text, useToast } from '@chakra-ui/react';
import { useApolloClient, useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import FormInput from '../../custom-components/Inputs/FormInput';
import ZaraButton from '../../custom-components/buttons/Button';

import { phoneSchema } from '../../../utils/schemas';
import { INPUT_PROPS } from '../../../utils/constants';
import { TextNotification } from '../../../graphql/mutations/TextNotification';
import { useUserData } from '../../../context/UserDataProvider';

export const textNotificationsSchema = z.object({
    phoneNumber: phoneSchema,
});

const TextNotifications = ({ data, agentId }) => {
    const { showError } = useError();
    const toast = useToast();
    const { userData: userDataFromContext } = useUserData();
    const myUserData = userDataFromContext || null;
    const hasSubscription = myUserData?.db_subscription_information != null;

    const apolloClient = useApolloClient();

    const [onTextNotification, { loading }] = useMutation(TextNotification, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const { control, handleSubmit, watch, setValue } = useForm({
        defaultValues: {
            phoneNumber: '',
        },
        mode: 'onSubmit',
        resolver: zodResolver(textNotificationsSchema),
    });

    useEffect(() => {
        if (data) {
            const { phoneNumber } = data;
            //TODO add initial phone number
            setValue('phoneNumber', phoneNumber);
        }
    }, [data, setValue]);

    const active = data?.active ?? false;



    const phoneNumber = watch('phoneNumber');

    async function onSubmit(data) {
        const variables = {
            active: active,
            phoneNumber: data?.phoneNumber?.includes('+') ? data.phoneNumber : '+' + data?.phoneNumber,
        };

        try {
            await onTextNotification({
                variables: { ...variables, agentId: agentId },
                onCompleted(data) {
                    if (data.textNotification.__typename === 'SuccessType') {
                        toast({
                            status: 'success',
                            title: data.textNotification.message,
                            isClosable: true,
                        });

                        apolloClient.cache.modify({
                            id: `AgentType:${agentId}`,
                            fields: {
                                textNotification() {
                                    return variables;
                                },
                            },
                        });
                    } else {
                        toast({
                            status: 'error',
                            title: data.textNotification.message,
                            isClosable: true,
                        });
                    }
                },
            });
        } catch (error) { }
    }

    async function changeTransfer(isActive, phoneNumber) {
        const variables = {
            active: isActive,
            phoneNumber: '+' + phoneNumber,
        };
        try {
            await onTextNotification({
                variables: { ...variables, agentId: agentId },
                onCompleted(data) {
                    if (data.textNotification.__typename === 'SuccessType') {
                        apolloClient.cache.modify({
                            id: `AgentType:${agentId}`,
                            fields: {
                                textNotification() {
                                    return variables;
                                },
                            },
                        });
                    }
                },
            });
        } catch (error) { }
    }

    return (
        <Box as='form' onSubmit={handleSubmit(onSubmit)}>
            <Flex
                fontSize={{ base: '24px', sm: '28px' }}
                lineHeight='28px'
                fontWeight={{ base: '400', sm: '700' }}
                textAlign='left'
                gap='10px'
                alignItems='center'
                mb='10px'
            >
                <Switch
                    isDisabled={loading}
                    isChecked={active}
                    onChange={() => changeTransfer(!active, phoneNumber)}
                    colorScheme='blackAlpha'
                />
                <Text fontSize='md'>Text Notifications</Text>
            </Flex>
            {active && (
                <Stack>
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                        <FormInput
                            control={control}
                            label='Phone Number for Text Notifications'
                            controllName={INPUT_PROPS.PHONE_NUMBER.CONTROLL_NAME}
                            placeholder={INPUT_PROPS.PHONE_NUMBER.PLACEHOLDER}
                        />
                    </div>
                    <Stack alignItems='start'>
                        <ZaraButton
                            isLoading={loading}
                            type='submit'
                            isDisabled={!phoneNumber?.trim().length || loading || !data?.active || !hasSubscription}
                            w='auto'
                            fontSize='md'
                        >
                            {!hasSubscription ? 'Add a Credit Card to use this Feature' : 'Save'}
                        </ZaraButton>
                    </Stack>
                </Stack>
            )}
        </Box>
    );
};

export default TextNotifications;
